// s84 (Fix 2, Option D): spec-aware suppression of legacy all-empty scaffold rows.
//
// A pre-cascade seeder left all-fields-blank `is_seeded=true` rows in several sections;
// the table/expandable renderer materializes any rowId with >=1 entry, so those drew as
// blank rows. We hide them — EXCEPT where a section's spec intentionally declares an
// empty `{}` starter row, which must keep rendering its blank starter rows.
//
// 🔴 s177 (A1, D-177.4) — the exemption set is now EMPTY, and the old comment naming
// "financial-business-self-employment + the 5 children-* detail sections" was already wrong
// before this change: the children-* sections had moved to `defaultRows: null`, which is not
// an array, so `sectionDeclaresEmptyStarter` had been returning false for them for some time.
// financial-business-self-employment was the last real member and its two `{}` starters
// retired in A1. `declaresEmptyStarter` is therefore a constant `false` today. The parameter
// is KEPT rather than deleted: it is a real distinction the spec can re-declare at any time,
// and removing it would only re-open the question later. [guard-change: D-177.4]
//
// Kept pure + framework-free so the predicate is unit-testable independently of the
// component. Mirrors the spec-aware cleanup SQL: match is per-row (value emptiness +
// is_seeded) gated by the section's defaultRows shape.
//
// Relocated to shared/ (s123, Build 4b-1) so the executor task-binding resolver
// (server-side) applies the SAME predicate the planner UI does — G7, never fork.
// `client/src/lib/table-row-visibility.ts` re-exports these for existing client imports.

import { isHideableLockedBuiltIn, isLockedBuiltIn, isHeaderRow, isSeededDefaultRow } from "@shared/constants/locked-rows";
import { entryUserActioned } from "@shared/lib/entry-provenance";

/** A row holds "real" user data: ≥1 non-seeded entry with a non-empty value. Mirrors
 *  `rowHasUserData` (row-completion.ts) but reads straight off the row's entries.
 *  Exported (s139) so the estate-snapshot reader drops untouched seeded scaffold rows
 *  using the SAME engaged-vs-scaffold rule the planner UI uses (G7, never fork). */
export function rowHasUserDataEntries(
  rowEntries: Array<{ value?: string | null; isSeeded?: boolean | null }>,
): boolean {
  return rowEntries.some(
    (e) => e.isSeeded !== true && (e.value ?? "").toString().trim() !== "",
  );
}

/**
 * Stable-partition a section's rowIds so user-data rows render ABOVE untouched
 * scaffold rows (s98 Fix C). Fan-out / user-added rows are created later than the
 * seeded scaffolds, so the API returns them last and they'd otherwise appear at the
 * bottom of the list.
 *
 * A row is "promoted" iff it has user data AND is neither a locked built-in row
 * (`__builtin__`, e.g. Government IDs — their deliberate template order must hold)
 * nor a section-header row (`__header__`). Relative order within each group is
 * preserved (stable), so this only lifts genuine user content above scaffolding.
 */
export function partitionUserRowsFirst(
  rowIds: string[],
  getEntries: (rowId: string) => Array<{ value?: string | null; isSeeded?: boolean | null }>,
): string[] {
  const promoted: string[] = [];
  const rest: string[] = [];
  for (const rowId of rowIds) {
    const isUserRow =
      !isLockedBuiltIn(rowId) &&
      !isHeaderRow(rowId) &&
      rowHasUserDataEntries(getEntries(rowId));
    (isUserRow ? promoted : rest).push(rowId);
  }
  return [...promoted, ...rest];
}

/** A section "declares an empty starter row" if any spec defaultRow has no non-empty
 *  field value (i.e. `{}` or every value blank). Same test as the cleanup SQL's
 *  `NOT EXISTS (... non-empty value ...)`. */
export function sectionDeclaresEmptyStarter(
  defaultRows: Array<Record<string, string>> | null | undefined,
): boolean {
  return (
    Array.isArray(defaultRows) &&
    defaultRows.some(
      (r) =>
        r != null &&
        typeof r === "object" &&
        Object.values(r).every((v) => v == null || String(v).trim() === ""),
    )
  );
}

/** A rowId is a legacy blank artifact (→ hide) iff every entry's value is empty/blank
 *  AND NO entry has been user-actioned AND the section does not declare an empty starter row.
 *  - Labeled scaffold (a non-empty value, e.g. id_type) → not all-empty → shown.
 *  - User mid-edit row (any entry is_seeded=false) → user-actioned → shown.
 *  - 🔴 s164 (PROVENANCE-SEMANTICS-1, D-164): a DECLINED row → user-actioned → shown. A decline
 *    stores no value, so under the old `every(isSeeded === true)` test a declined scaffold row
 *    was still "all-empty and all-seeded" and would have been hidden the moment decline stopped
 *    flipping provenance — the decline would have made its own row vanish. `entryUserActioned`
 *    is what keeps it on screen. Behaviour-neutral pre-writer-change: a declined entry was
 *    already isSeeded=false, so this hid nothing it hides now. */
export function isLegacyBlankRow(
  rowEntries: Array<{ value?: string | null; isSeeded?: boolean | null; declined?: boolean | null }>,
  declaresEmptyStarter: boolean,
): boolean {
  if (declaresEmptyStarter) return false;
  if (rowEntries.length === 0) return false;
  const allEmpty = rowEntries.every(
    (e) => (e.value ?? "").toString().trim() === "",
  );
  const noneUserActioned = !rowEntries.some(entryUserActioned);
  return allEmpty && noneUserActioned;
}

/** A rowId is soft-hidden (→ suppress in the planner) iff EVERY entry on the row has
 *  `is_hidden = true`. This is the read-path honouring of `generic_entries.is_hidden`,
 *  which the planner expandable/table render path historically ignored (so a "removed"
 *  row persisted — s84/s106 MIG-1). Making soft-delete-via-is_hidden actually work.
 *  - Locked built-in rows (`__builtin__…`) are EXEMPT — their template order must hold,
 *    so they never vanish even if flagged — EXCEPT in the sections named by
 *    `HIDEABLE_LOCKED_SECTIONS` (s223, D-222.10 part 5: the interview's Government ID
 *    screen hides the built-in row for an ID the planner says they do not have, and
 *    unhides it when they say they do). Every other locked row keeps the exemption.
 *  - A partially-hidden row (any entry is_hidden=false) is NOT suppressed.
 *  - Independent of `isLegacyBlankRow`: this catches NON-blank hidden rows (e.g. a
 *    deactivated scaffold that still carries a type value). */
export function isRowHidden(
  rowId: string,
  rowEntries: Array<{ isHidden?: boolean | null }>,
): boolean {
  if (isLockedBuiltIn(rowId) && !isHideableLockedBuiltIn(rowId)) return false;
  if (rowEntries.length === 0) return false;
  return rowEntries.every((e) => e.isHidden === true);
}

/** A row is a real, visible user record for binding purposes: not soft-hidden, not a
 *  legacy blank artifact, and carries ≥1 non-seeded non-empty value. Used by the
 *  executor task-binding resolver (Build 4b-1) to surface only genuine planner records. */
export function rowIsVisibleUserRecord(
  rowId: string,
  rowEntries: Array<{ value?: string | null; isSeeded?: boolean | null; isHidden?: boolean | null; declined?: boolean | null }>,
  declaresEmptyStarter: boolean,
): boolean {
  if (isRowHidden(rowId, rowEntries)) return false;
  if (isLegacyBlankRow(rowEntries, declaresEmptyStarter)) return false;
  return rowHasUserDataEntries(rowEntries);
}

/** 🔴 s177 (D-177.17): a row is an UNTOUCHED SUGGESTION — the only thing the shaded legend
 *  legitimately describes. It must be a seeded scaffold, still visible, not a legacy blank
 *  artifact, and carry NO planner data.
 *
 *  The last clause is the one that matters and the one the first cut missed. After the s177
 *  retirement (D-177.4) the migration hid every untouched seeded row, so the only seeded rows
 *  still on screen are ENGAGED ones — real records occupying a seeded slot (a filled address in
 *  `__seeded__assets-properties__0`). A gate that asked merely "is a seeded row visible?" therefore
 *  pointed the legend at rows the planner had already completed and called them recommendations.
 *
 *  Composed from the predicates above rather than restating them, so it cannot drift from the
 *  visibility and engagement rules the migration and the completion math use. */
export function isUntouchedSuggestionRow(
  rowId: string,
  rowEntries: Array<{ value?: string | null; isSeeded?: boolean | null; isHidden?: boolean | null; declined?: boolean | null }>,
  declaresEmptyStarter: boolean,
): boolean {
  if (!isSeededDefaultRow(rowId)) return false;
  if (isRowHidden(rowId, rowEntries)) return false;
  if (isLegacyBlankRow(rowEntries, declaresEmptyStarter)) return false;
  // 🔴 "Untouched" means NO planner action of any kind — not merely "no data". `entryUserActioned`
  // is the canonical test (D-164): it is true for a typed value AND for a decline. A DECLINED row
  // has been answered — the planner said "I don't have this" — so it is no longer an outstanding
  // recommendation, and it renders grey as "Not available", which is not what the legend describes.
  // Using the one predicate rather than `!rowHasUserDataEntries` also means the data case and the
  // decline case cannot drift apart.
  return !rowEntries.some(entryUserActioned);
}
